import axios from 'axios';
import { ENV } from '@configs/env';
import { useAuthStore } from '@stores/auth.store';
import { refreshToken } from '@services/refresh.service';

const http = axios.create({
    baseURL: ENV.API_URL,
    withCredentials: true
});

let refreshPromise = null;

export const initializeHttpInterceptors = () => {
    http.interceptors.request.use((config) => {
        const token = useAuthStore.getState().accessToken;
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
    });

    http.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

                if (originalRequest._retryCount > 3) {
                    console.error('Max refresh retries reached');
                    useAuthStore.getState().logout();
                    return Promise.reject(error);
                }

                if (!refreshPromise) {
                    refreshPromise = (async () => {
                        try {
                            const refreshTokenValue = useAuthStore.getState().refreshToken;
                            const setAuthState = useAuthStore.setState;
                            const logout = useAuthStore.getState().logout;

                            // console.log('Attempting refresh with token:', refreshTokenValue); // <-- debug
                            const newTokens = await refreshToken(refreshTokenValue, setAuthState, logout);

                            if (!newTokens || !newTokens.accessToken) {
                                logout();
                                return Promise.reject(new Error('Refresh failed'));
                            }

                            return newTokens;
                        } catch (err) {
                            console.error('Token refresh error:', err);
                            useAuthStore.getState().logout();
                            return Promise.reject(err);
                        } finally {
                            refreshPromise = null;
                        }
                    })();
                }

                try {
                    const newTokens = await refreshPromise;
                    originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
                    return http(originalRequest);
                } catch (err) {
                    return Promise.reject(err);
                }
            }

            return Promise.reject(error);
        }
    );
};

export default http;

